export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Editor' | 'Viewer';
};

export type AccountRecord = {
  id: string;
  name: string;
  email: string;
  role: AppUser['role'];
  salt: string;
  passwordHash: string;
};

export type AppProject = {
  id: string;
  name: string;
  owner: string;
  status: 'Production' | 'Staging' | 'Review';
  health: number;
  deploys: number;
  updated: string;
};

export type Workspace = {
  id: string;
  name: string;
  region: string;
};

export type MessageFolder = 'inbox' | 'sent' | 'drafts' | 'archive';

export type AppMessage = {
  id: string;
  threadId: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  createdAt: string;
  folder: MessageFolder;
  label: string;
  unread: boolean;
  starred: boolean;
};

export type TeamInvite = {
  id: string;
  email: string;
  role: AppUser['role'];
  createdAt: string;
  status: 'pending';
};

export type AnalyticsReport = {
  id: string;
  name: string;
  tab: 'Overview' | 'Acquisition' | 'Retention' | 'Revenue';
  period: string;
  format: string;
  createdAt: string;
};

export type DashboardAnnotation = {
  id: string;
  text: string;
  createdAt: string;
};

export type AppNotification = {
  id: string;
  title: string;
  message: string;
  tone: 'success' | 'info' | 'warning';
  createdAt: string;
  read: boolean;
};

export type LicenseRequest = {
  id: string;
  license: string;
  priceUsd: number;
  name: string;
  email: string;
  company: string;
  createdAt: string;
};

export type AppSettings = {
  workspaceName: string;
  timezone: string;
  previewDeployments: boolean;
  autoMerge: boolean;
  weeklySummary: boolean;
  incidentNotifications: boolean;
  requireMfa: boolean;
  trustedDevicesOnly: boolean;
  auditSensitiveActions: boolean;
  integrations: Record<string, boolean>;
  billingPlan: 'Starter' | 'Pro' | 'Scale';
};

export type AppState = {
  account: AccountRecord | null;
  user: AppUser | null;
  projects: AppProject[];
  workspaces: Workspace[];
  activeWorkspaceId: string;
  settings: AppSettings;
  messages: AppMessage[];
  invites: TeamInvite[];
  reports: AnalyticsReport[];
  annotations: DashboardAnnotation[];
  notifications: AppNotification[];
  licenseRequests: LicenseRequest[];
};

const KEY = 'gpt-build-app-state-v3';

const seedProjects: AppProject[] = [
  { id: 'atlas', name: 'Atlas Command', owner: 'Core', status: 'Production', health: 98, deploys: 41, updated: '3 min ago' },
  { id: 'northstar', name: 'Northstar AI', owner: 'Labs', status: 'Production', health: 94, deploys: 29, updated: '12 min ago' },
  { id: 'beacon', name: 'Beacon Mobile', owner: 'Product', status: 'Staging', health: 87, deploys: 18, updated: '36 min ago' },
  { id: 'signal', name: 'Signal Graph', owner: 'Data', status: 'Production', health: 99, deploys: 64, updated: '1 hr ago' },
  { id: 'prism', name: 'Prism Billing', owner: 'Finance', status: 'Review', health: 91, deploys: 11, updated: '2 hr ago' },
  { id: 'mercury', name: 'Mercury Edge', owner: 'Infra', status: 'Production', health: 96, deploys: 53, updated: '4 hr ago' },
];

const seedMessages: AppMessage[] = [
  {
    id: 'msg-launch',
    threadId: 'thread-launch',
    sender: 'Priya Shah',
    recipient: 'Workspace Admin',
    subject: 'Q4 launch readiness',
    body: 'The rollout checklist is green except for one analytics dependency. I added the owner and mitigation plan to the launch brief. If the event pipeline remains stable through the next window, Product is comfortable proceeding.',
    createdAt: '2026-09-26T09:42:00.000Z',
    folder: 'inbox',
    label: 'Product',
    unread: true,
    starred: true,
  },
  {
    id: 'msg-design',
    threadId: 'thread-design',
    sender: 'Mina Park',
    recipient: 'Workspace Admin',
    subject: 'Design system review',
    body: 'The component audit is complete. Keyboard navigation and contrast are green across the high-traffic surfaces. I left two low-priority density suggestions in the design notes.',
    createdAt: '2026-09-26T08:17:00.000Z',
    folder: 'inbox',
    label: 'Design',
    unread: true,
    starred: false,
  },
  {
    id: 'msg-latency',
    threadId: 'thread-latency',
    sender: 'Eli Brooks',
    recipient: 'Workspace Admin',
    subject: 'Edge latency follow-up',
    body: 'P95 is back under target across all regions after the cache policy change. Error rate remained flat during the rollout and the incident can be closed.',
    createdAt: '2026-09-25T21:04:00.000Z',
    folder: 'inbox',
    label: 'Infra',
    unread: false,
    starred: false,
  },
  {
    id: 'msg-cohort',
    threadId: 'thread-cohort',
    sender: 'Noah Chen',
    recipient: 'Workspace Admin',
    subject: 'Experiment cohort export',
    body: 'The cleaned cohort summary and retention breakdown are ready. I normalized the acquisition source mapping so the week-over-week comparison is now consistent.',
    createdAt: '2026-09-25T17:11:00.000Z',
    folder: 'inbox',
    label: 'Data',
    unread: false,
    starred: false,
  },
];

const seedNotifications: AppNotification[] = [
  {
    id: 'note-deploy',
    title: 'Deployment complete',
    message: 'Atlas Command reached production.',
    tone: 'success',
    createdAt: '2026-09-26T09:39:00.000Z',
    read: false,
  },
  {
    id: 'note-budget',
    title: 'Capacity threshold',
    message: 'Edge compute reached 78% of the configured target.',
    tone: 'warning',
    createdAt: '2026-09-26T09:24:00.000Z',
    read: false,
  },
  {
    id: 'note-report',
    title: 'Report ready',
    message: 'September executive report is available.',
    tone: 'info',
    createdAt: '2026-09-26T08:42:00.000Z',
    read: false,
  },
];

const initialWorkspace: Workspace = {
  id: 'primary',
  name: 'GPT Build Workspace',
  region: 'Canada Central',
};

export const initialState: AppState = {
  account: null,
  user: null,
  projects: seedProjects,
  workspaces: [initialWorkspace],
  activeWorkspaceId: initialWorkspace.id,
  settings: {
    workspaceName: initialWorkspace.name,
    timezone: 'America/Toronto',
    previewDeployments: true,
    autoMerge: false,
    weeklySummary: true,
    incidentNotifications: true,
    requireMfa: true,
    trustedDevicesOnly: false,
    auditSensitiveActions: true,
    integrations: {
      'Git provider': true,
      Analytics: true,
      Email: true,
      Payments: true,
    },
    billingPlan: 'Pro',
  },
  messages: seedMessages,
  invites: [],
  reports: [],
  annotations: [],
  notifications: seedNotifications,
  licenseRequests: [],
};

export function loadAppState(): AppState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as Partial<AppState>;
    return {
      ...initialState,
      ...parsed,
      settings: { ...initialState.settings, ...parsed.settings },
      projects: parsed.projects ?? initialState.projects,
      workspaces: parsed.workspaces?.length ? parsed.workspaces : initialState.workspaces,
      messages: parsed.messages ?? initialState.messages,
      invites: parsed.invites ?? initialState.invites,
      reports: parsed.reports ?? initialState.reports,
      annotations: parsed.annotations ?? initialState.annotations,
      notifications: parsed.notifications ?? initialState.notifications,
      licenseRequests: parsed.licenseRequests ?? initialState.licenseRequests,
    };
  } catch {
    return initialState;
  }
}

export function saveAppState(state: AppState): void {
  localStorage.setItem(KEY, JSON.stringify(state));
}

export function resetWorkspaceData(state: AppState): AppState {
  return {
    ...initialState,
    account: state.account,
    user: state.user,
    licenseRequests: state.licenseRequests,
  };
}

export async function derivePasswordHash(password: string, salt: string): Promise<string> {
  const bytes = new TextEncoder().encode(salt + ':' + password);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function createSalt(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
